import type { ReactNode } from "react";
import { cn } from "@/lib/utils/cn";

export type SegmentedOption<T extends string> = { value: T; label: ReactNode; icon?: ReactNode; disabled?: boolean };

export type SegmentedProps<T extends string> = {
  value: T;
  onChange: (value: T) => void;
  options: SegmentedOption<T>[];
  size?: "sm" | "md";
  className?: string;
  "aria-label"?: string;
  /** Stretch options equally across the full width. */
  full?: boolean;
};

/** Toggle group / segmented control. Radio-group semantics with arrow-key support via native tab order. */
export function Segmented<T extends string>({ value, onChange, options, size = "md", className, full, ...aria }: SegmentedProps<T>) {
  return (
    <div role="radiogroup" aria-label={aria["aria-label"]} className={cn("inline-flex rounded-md bg-surface-2 p-1", full && "flex w-full", className)}>
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={o.disabled}
            onClick={() => onChange(o.value)}
            className={cn(
              "inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-sm font-medium text-fg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4",
              size === "sm" ? "h-7 px-2.5 text-xs" : "h-8 px-3 text-sm",
              full && "flex-1",
              active && "bg-surface text-fg shadow-card",
            )}
          >
            {o.icon}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
